import React, { useState, useEffect } from 'react';

import Shimmer from '~/src/atoms/Shimmer';
import tw from 'twin.macro';
import dynamic from 'next/dynamic';
import CallToAction from '~/src/organisms/CallToAction';
import PlusIcon from '../../../public/bb2assets/images/svgComponents/Plus';
import MinusIcon from '../../../public/bb2assets/images/svgComponents/Minus';
import Label from '~/src/atoms/Label';
import Loader from '~/src/molecules/Loader';
import Link from '~/src/atoms/Link';
import { DeckContext } from './DeckContext';
import { SaveForLaterButton, NotifyMeButton } from '~/src/molecules/CtaButtons';
import { getItemFromSessionStorage } from '~/src/libs/sessionStorage';
import { basketInteractionSnowplowEvent, CitrusAdsProductIntercations } from './SnowplowEvents';
import { triggerCartGTMEvent } from './GTMEvents';
import noop from 'lodash/noop';
import { triggerSnowplowEventProductInteractions, updateReferrerContext } from '@bigbasket/snowplow';
import { CookieService } from '~/src/libs/cookie';

const Tooltip = dynamic(() => import('~/src/molecules/Tooltip/newToolTip'), { ssr: false });

const Button = dynamic(() => import('~/src/atoms/Button'), {
  ssr: false,
  loading: () => <Shimmer css={tw`w-32 h-8 bg-silverSurfer-200 rounded-2xs`} />,
});

const CtaOnDect = ({
  Offercard = false,
  onChange = noop,
  options = {},
  onClick = () => {},
  extraPayload = {},
  listType = '',
  screenInPagePosition = '',
  screenInPageContext = '',
  screenItemName = '',
  screenItemPosition = '',
  analyticsAttrs = {},
  screenType = '',
  screenTypeId = '',
  tab = {},
  citrusAdId = [],
  parentRef,
  enableOfferEntryPoint,
}) => {
  const { packSizes = [], packSelected = {}, changeQuantity } = React.useContext(DeckContext);
  const [eventName, setEventName] = useState('');

  const isBotGlobalsa = CookieService.getCookie('isbotglobalsa');

  useEffect(() => {
    if (eventName) {
      const eventData = {
        eventName,
        selectedSKU: packSelected,
        listType,
        screenInPageContext,
        screenInPagePosition,
        screenItemName,
        screenItemPosition,
        analyticsAttrs,
        tab,
        screenTypeId,
        citrusAdId,
      };
      triggerCartGTMEvent(eventData);
      basketInteractionSnowplowEvent(eventData);
    }
    setEventName('');
  }, [packSelected.quantity]);

  if (packSelected?.availability?.not_for_sale) {
    return <div className="w-full h-8"></div>;
  }

  const isProductNotAvailable = packSelected?.availability?.avail_status === '000' || packSelected?.availability?.avail_status === '010' ? true : false;

  const handleChange = (e) => {
    onChange({ ...e, id: packSelected?.id });
    if (e?.data?.response?.sku?.tot_qty >= 0) {
      changeQuantity(e?.data?.response?.sku?.tot_qty);
    }
  };

  const handleRemoveItem = (e, removeFromSFL) => {
    e.stopPropagation();
    onClick();
    removeFromSFL(packSizes?.[0]?.id);
    const snowplowEventData = {
      EventName: 'savedforlater_removed',
      ScreenType: screenType,
      SkuID: Number(packSizes?.[0]?.id),
      InPageContext: 'saveforlater',
    };

    triggerSnowplowEventProductInteractions(snowplowEventData);
  };

  const handleAddToSFL = (addToSFL) => {
    const eventData = {
      EventName: 'saveforlater_clicked',
      selectedSKU: packSelected,
      analyticsAttrs,
      citrusAdId,
    };
    CitrusAdsProductIntercations(eventData);
    onClick();
    addToSFL();
  };

  return (
    <CallToAction
      payload={{ id: typeof packSelected?.id === 'string' ? parseInt(packSelected?.id) : packSelected?.id, invInfo: packSelected?.inv_info  }}
      onChange={handleChange}
      extras={{...extraPayload, invInfo: packSelected?.inv_info}}>
      {({ addToBasket, addToSFL, incQuantity, decQuantity, removeFromSFL, notifyMe, loader }) => {
        const isAlreadyClicked = getItemFromSessionStorage(packSelected?.id) === 'sfl';
        const handleSFL = isAlreadyClicked ? (e) => handleRemoveItem(e, removeFromSFL) : () => handleAddToSFL(addToSFL);

        const handleAddToBasket = (e) => {
          e.stopPropagation();
          onClick();
          addToBasket();
          setEventName('Basket_Add');
        };

        const handleQuantityIncrement = () => {
          incQuantity();
          onClick();
          setEventName('Basket_increment');
        };

        const handleQuantityDecrement = () => {
          decQuantity();
          onClick();
          setEventName('Basket_decrement');
        };

        const handleViewProductDetails = () => {
          updateReferrerContext('');
          window.open(packSelected?.absoluteUrl, '_blank');
        };

        const SFLViewProductDetails = () => {
          updateReferrerContext('');
        };

        return (
          <div className={`flex flex-col ${Offercard && `mt-1.5`}`}>
            <div className="flex">
              {isProductNotAvailable ? (
                <>
                  {options.showSFLbutton && (
                    <div css={tw`lg:w-8 lg:h-8 xl:w-9 xl:h-9 mr-2.5 hover:shadow-2 rounded-2xs`}>
                      <Tooltip
                        position="bottom"
                        panelStyle={screenItemPosition === 1 ? { top: '45px', left: 0 } : { top: '45px' }}
                        parentRef={parentRef}
                        enableOfferEntryPoint={enableOfferEntryPoint}
                        arrowStyle={screenItemPosition === 1 ? { right: 'unset', left: 0 } : { right: 'calc(50% - 8px)' }}
                        panelClasses={screenItemPosition != 1 ? 'lg:right-[-100%] xl:right-[-87%]' : ''}
                        buttonStyle="h-8"
                        animationOrigin="top-right"
                        panelContent={() => <span style={{ fontSize: '12px' }}>{isAlreadyClicked ? 'Remove Product' : 'Save For Later'}</span>}>
                        {!isBotGlobalsa && (
                          <SaveForLaterButton id={packSelected?.id} onClick={handleSFL} isLoading={loader.status && loader.label === 'Saving...'} />
                        )}
                      </Tooltip>
                    </div>
                  )}
                  <NotifyMeButton
                    css={tw`w-full lg:h-8 xl:h-9 lg:text-md xl:text-base`}
                    id={packSelected?.id}
                    isLoading={loader.label === 'Notified...' && loader.status}
                    onClick={notifyMe}>
                    {packSelected?.availability?.button}
                  </NotifyMeButton>
                </>
              ) : (
                <>
                  {options.showSFLbutton && (
                    <div css={tw`lg:w-8 lg:h-8 xl:w-9 xl:h-9 mr-2.5 hover:shadow-2 rounded-2xs`}>
                      <Tooltip
                        position="bottom"
                        panelStyle={screenItemPosition === 1 ? { top: '45px', left: 0 } : { top: '45px' }}
                        parentRef={parentRef}
                        enableOfferEntryPoint={enableOfferEntryPoint}
                        arrowStyle={screenItemPosition === 1 ? { right: 'unset', left: 0 } : { right: 'calc(50% - 8px)' }}
                        panelClasses={screenItemPosition != 1 ? 'lg:right-[-100%] xl:right-[-87%]' : ''}
                        buttonStyle="h-8"
                        animationOrigin="top-right"
                        panelContent={() => <span style={{ fontSize: '12px' }}>{isAlreadyClicked ? 'Remove Product' : 'Save For Later'}</span>}>
                        {!isBotGlobalsa && (
                          <SaveForLaterButton id={packSelected?.id} onClick={handleSFL} isLoading={loader.status && loader.label === 'Saving...'} />
                        )}
                      </Tooltip>
                    </div>
                  )}
                  <div className="relative flex flex-col w-full lg:h-8 xl:h-9 overflow-hidden">
                    <div className="flex items-center justify-between w-full h-full">
                      <Button
                        onClick={handleQuantityDecrement}
                        className="group"
                        variant={'rossoCorsa-500-filled-small'}
                        css={[
                          tw`w-1/3 h-full px-0 py-0 duration-300 transform rounded-tr-none rounded-br-none opacity-100 rounded-tl-2xs rounded-bl-2xs`,
                          packSelected?.quantity <= 0 && tw`translate-x-full opacity-0`,
                        ]}>
                        <MinusIcon css={tw`text-white fill-current`} />
                      </Button>
                      <div
                        css={[
                          tw`relative flex items-center justify-center w-1/3 h-full px-0 border-2 border-solid opacity-100 border-rossoCorsa-500 text-md leading-xs text-rossoCorsa-500`,
                          packSelected?.quantity <= 0 && tw`opacity-0`,
                        ]}>
                        <Label css={tw`absolute xl:text-base  font-semibold transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2`}>
                          {packSelected?.quantity}
                        </Label>
                        <Loader color="#cc0000" loading={(loader.label === 'Adding...' || loader.label === 'Reducing...') && loader.status} size={25} />
                      </div>
                      <Button
                        onClick={handleQuantityIncrement}
                        className="group"
                        variant={'rossoCorsa-500-filled-small'}
                        css={[
                          tw`w-1/3 h-full px-0 py-0 duration-300 transform rounded-tl-none rounded-bl-none opacity-100 rounded-tr-2xs rounded-br-2xs`,
                          packSelected?.quantity <= 0 && tw`-translate-x-full opacity-0`,
                        ]}>
                        <PlusIcon css={tw`text-white fill-current`} />
                      </Button>
                    </div>
                    <Button
                      variant={loader.status && loader.label === 'Adding...' ? 'rossoCorsa-50-filled' : 'rossoCorsa-500-outline-small'}
                      css={[
                        tw`xl:text-base xl:leading-sm lg:text-md xl:py-2 px-0 absolute top-0 left-0 z-1 w-full h-full duration-300 transform translate-y-0 border opacity-100`,
                        packSelected?.quantity > 0 && tw`scale-0 translate-y-full opacity-0`,
                      ]}
                      onClick={(e) => handleAddToBasket(e)}>
                      {/* {!(loader.status && loader.label === 'Adding...') && `packSelected?.availability?.button`} */}
                      {loader.status && loader.label === 'Adding...' ? loader.label : packSelected?.availability?.button}
                    </Button>
                  </div>
                </>
              )}
            </div>
            {options?.showPdLinkableThreedots && (
              <div className={`flex justify-end`} onClick={handleViewProductDetails}>
                <Tooltip
                  position="bottom"
                  parentRef={parentRef}
                  enableOfferEntryPoint={enableOfferEntryPoint}
                  animationOrigin="top-right"
                  panelStyle={{ top: '26px', right: '-4px' }}
                  panelContent={() => <span style={{ fontSize: '12px' }}>View product details</span>}>
                  <div css={tw`flex w-6 h-6 box-content justify-between items-center pt-1 rounded-3xl`}>
                    <div className="w-1 h-1 bg-darkOnyx-900 rounded-full" />
                    <div className="w-1 h-1 bg-darkOnyx-900 rounded-full" />
                    <div className="w-1 h-1 bg-darkOnyx-900 rounded-full" />
                  </div>
                </Tooltip>
              </div>
            )}
            {options?.showThreeDots && (
              <div className="flex justify-end w-full">
                <div className={`cursor-pointer`}>
                  <Tooltip
                    position="bottom"
                    parentRef={parentRef}
                    enableOfferEntryPoint={enableOfferEntryPoint}
                    arrowBackGround="#FFFFFF"
                    panelStyle={{ top: '26px', background: '#FFFFFF', boxShadow: '0 0 4px rgb(0 0 0 / 17%)', right: 0 }}
                    panelContent={() => (
                      <ul className="text-darkOnyx-900 text-xm">
                        <li className="text-left py-0.5 border-b cursor-pointer border-silverSurfer-300 focus:outline-none">
                          <Button
                            onClick={(e) => handleRemoveItem(e, removeFromSFL)}
                            css={tw`flex justify-start w-full py-1 pl-1 m-0 bg-transparent font-regular  border-none text-darkOnyx-400 hover:text-darkOnyx-800 text-md`}>
                            Remove Product
                          </Button>
                        </li>
                        <li className="text-left py-0.5 cursor-pointer focus:outline-none">
                          <Link href={packSelected?.absoluteUrl}>
                            <Button
                              onClick={SFLViewProductDetails}
                              css={tw`flex justify-start w-full py-1 pl-1 m-0 bg-transparent font-regular  border-none text-darkOnyx-400 hover:text-darkOnyx-800 text-md`}>
                              View Product Details
                            </Button>
                          </Link>
                        </li>
                      </ul>
                    )}>
                    <div css={tw`flex w-4.5 mt-2 box-content justify-between p-1 hover:bg-silverSurfer-200 rounded-3xl`}>
                      <div className="w-1 h-1 bg-black rounded-full" />
                      <div className="w-1 h-1 bg-black rounded-full" />
                      <div className="w-1 h-1 bg-black rounded-full" />
                    </div>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </CallToAction>
  );
};

export default CtaOnDect;
